/**
 * Which tenant this page was loaded for, sent on every write.
 *
 * A platform admin's session can move between tenants (Change tenant) while
 * other tabs are still showing the tenant they were loaded for, and every tab
 * shares the one session cookie. The server refuses a write whose
 * X-Switchboard-Tenant names a different tenant than the session is in now
 * (409 TENANT_CHANGED), and a switched session's write that names none. The
 * page then reloads into the tenant the session is in.
 *
 * Every tab also hears about a switch made in another tab straight away, on a
 * BroadcastChannel, and reloads before anyone can act on stale data.
 */

export const TENANT_PIN_HEADER = 'X-Switchboard-Tenant';

let pinnedTenantId: string | null = null;

/** Set from the session once it is known (AdminAuthContext). */
export function setTenantPin(tenantId: string | null | undefined): void {
  pinnedTenantId = tenantId ?? null;
}

export function getTenantPin(): string | null {
  return pinnedTenantId;
}

const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** The pin header for a request of this method, or nothing for a read. */
export function tenantPinHeaders(method: string | undefined): Record<string, string> {
  if (!pinnedTenantId || !WRITE_METHODS.has((method ?? 'GET').toUpperCase())) return {};
  return { [TENANT_PIN_HEADER]: pinnedTenantId };
}

/** Server answers that mean "this page is for a tenant the session is no longer in". */
const TENANT_MOVED_CODES = new Set(['TENANT_CHANGED', 'SWITCH_EXPIRED']);

export function isTenantMovedCode(code: unknown): boolean {
  return typeof code === 'string' && TENANT_MOVED_CODES.has(code);
}

const CHANNEL = 'switchboard-session';

/**
 * This page's own mark on what it announces. A BroadcastChannel delivers to
 * every other channel object with the name, this page's own listener
 * included, and that listener must not take its own announcement for a
 * switch made elsewhere: its reload to /admin would land after, and win over,
 * the reload to the page the switch was made for.
 */
const PAGE_ID = Math.random().toString(36).slice(2);

/**
 * The admin page to open after a switch made from a link to it (a call in
 * another tenant). Written only once the switch has been made, so a switch
 * given up leaves nothing behind, and read once.
 */
const AFTER_SWITCH_KEY = 'switchboard.afterSwitch';

export function rememberAfterSwitch(path: string): void {
  try { sessionStorage.setItem(AFTER_SWITCH_KEY, path); } catch { /* storage blocked: they land on /admin */ }
}

/** The remembered page, if it is one of this app's admin pages; cleared either way. */
function takeAfterSwitch(): string | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(AFTER_SWITCH_KEY);
    sessionStorage.removeItem(AFTER_SWITCH_KEY);
  } catch {
    return null;
  }
  // Only a path of our own: never another origin (`//host`, `/\host`) or scheme.
  if (!raw || !raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) return null;
  try {
    const url = new URL(raw, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    if (url.pathname !== '/admin' && !url.pathname.startsWith('/admin/')) return null;
    return url.pathname + url.search + url.hash;
  } catch {
    return null;
  }
}

/**
 * Reload into the tenant the session is in now, dropping everything on screen:
 * to the page the switch was made for, if there is one, otherwise /admin.
 */
export function reloadIntoCurrentTenant(): void {
  window.location.assign(takeAfterSwitch() ?? '/admin');
}

/** Tell every other tab that the session has moved tenant. */
export function announceTenantSwitch(): void {
  try {
    const channel = new BroadcastChannel(CHANNEL);
    channel.postMessage({ type: 'tenant-switched', from: PAGE_ID });
    channel.close();
  } catch {
    // No BroadcastChannel (very old browser): the pin still refuses stale writes.
  }
}

/** Run `onSwitched` when another tab moves the session. Returns the unsubscribe. */
export function onTenantSwitchElsewhere(onSwitched: () => void): () => void {
  try {
    const channel = new BroadcastChannel(CHANNEL);
    channel.onmessage = (event: MessageEvent) => {
      if (event.data?.type === 'tenant-switched' && event.data.from !== PAGE_ID) onSwitched();
    };
    return () => channel.close();
  } catch {
    return () => undefined;
  }
}
